import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/main.scss';
import './i18n';
import './index.css';
import { ApolloProviderWrapper } from './ApolloClient';
import './styles/react-grid-styles.scss';
import { Amplify } from 'aws-amplify';
import { authConfig } from './amplifyAuthConfig';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { isLocalhost, isReview } from './utils';
import { CookieStorage } from 'aws-amplify/utils';
import { BrowserRouter } from 'react-router-dom';
import { AuthenticationProvider } from './app/AuthenticationContext';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { App } from './app';
import { ignoredSentryEvents } from '@/ignoredSentryEvents';
import * as Sentry from '@sentry/react';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { formDevtoolsPlugin } from '@tanstack/react-form-devtools';

Sentry.init({
  enabled: import.meta.env.VITE_SENTRY_ENVIROMENT !== 'local',
  dsn: 'https://5166d9a1dfcdc13bbaecd7b1b972aaf2@o4508318398545920.ingest.de.sentry.io/4508675909419088',
  beforeSend(event) {
    if (ignoredSentryEvents(event)) {
      return null;
    }
    event.tags = {
      ...event.tags,
      device_width: window.innerWidth,
    };
    return event;
  },
  environment: import.meta.env.VITE_SENTRY_ENVIROMENT,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: 'light',
      autoInject: false,
    }),
  ],
  // Tracing: Capturing 1% of transactions
  tracesSampleRate: 0.01,
  tracePropagationTargets: ['localhost', /^https?:\/\/.*\.e2n\.de/],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0.1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});
Sentry.setTag('device_width', window.innerWidth);

Amplify.configure({ Auth: authConfig });
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: isLocalhost
      ? 'localhost'
      : isReview
        ? window.location.host
        : 'e2n.de',
    path: '/',
    expires: 365,
    secure: isLocalhost ? false : true,
    sameSite: 'strict',
  }),
);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <TanStackDevtools
      plugins={[formDevtoolsPlugin()]}
      config={{
        position: 'bottom-left',
      }}
    />
    <AuthenticationProvider>
      <ApolloProviderWrapper>
        <HelmetProvider>
          <Helmet defaultTitle="e2n Manager" titleTemplate="%s · e2n Manager" />
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </HelmetProvider>
      </ApolloProviderWrapper>
    </AuthenticationProvider>
  </React.StrictMode>,
);
